// AXIS company introduction — restrained, professional, white.
// Feels like the "producer" card before a film, not a mobile game logo.
// Structured so the placeholder wordmark can be swapped for an <img> logo
// via BRAND.company.logoImage without touching the animation.
import { useEffect, useState } from "react";
import { BRAND, INTRO_TIMING } from "@/config/game";

/**
 * @param {"in"|"hold"|"out"} phase
 */
export default function AxisLogoIntro({ phase }) {
    const [tracking, setTracking] = useState(0.32); // em letter-spacing

    // Subtle letterspacing expansion during hold
    useEffect(() => {
        if (phase === "in") setTracking(0.42);
        if (phase === "hold") {
            const t = setTimeout(() => setTracking(0.55), 60);
            return () => clearTimeout(t);
        }
        if (phase === "out") setTracking(0.62);
    }, [phase]);

    const opacity = phase === "out" ? 0 : phase === "in" ? 1 : 1;
    const scale = phase === "out" ? 1.015 : phase === "in" ? 1 : 1.004;

    const transitionIn = `${INTRO_TIMING.AXIS_INTRO_IN}ms`;
    const transitionOut = `${INTRO_TIMING.AXIS_INTRO_OUT}ms`;

    return (
        <div
            className="relative flex flex-col items-center select-none"
            style={{
                opacity,
                transform: `scale(${scale})`,
                transition:
                    phase === "out"
                        ? `opacity ${transitionOut} ease, transform ${transitionOut} ease`
                        : `opacity ${transitionIn} cubic-bezier(0.16, 1, 0.3, 1), transform 3200ms ease`,
            }}
        >
            {BRAND.company.logoImage ? (
                <img
                    src={BRAND.company.logoImage}
                    alt={BRAND.company.name}
                    className="w-[360px] max-w-[70vw]"
                    style={{
                        filter: "drop-shadow(0 0 8px rgba(255,255,255,0.08))",
                    }}
                />
            ) : (
                <div className="text-center">
                    <div
                        style={{
                            fontFamily: "'Rajdhani', 'Chakra Petch', sans-serif",
                            fontWeight: 300,
                            color: "#f3f4f6",
                            fontSize: "clamp(56px, 8vw, 96px)",
                            letterSpacing: `${tracking}em`,
                            transition: "letter-spacing 3400ms cubic-bezier(0.16, 1, 0.3, 1)",
                            textIndent: `${tracking}em`, // compensate to keep visually centered
                            filter: "drop-shadow(0 0 6px rgba(255,255,255,0.05))",
                            lineHeight: 1,
                        }}
                    >
                        {BRAND.company.name}
                    </div>

                    <div
                        className="mx-auto mt-8 h-px"
                        style={{
                            width: phase === "in" ? "10px" : phase === "hold" ? "160px" : "220px",
                            background:
                                "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                            transition: "width 2800ms cubic-bezier(0.16, 1, 0.3, 1)",
                        }}
                    />

                    <div
                        className="mt-4 text-[10px] font-light"
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            letterSpacing: "0.65em",
                            color: "rgba(255,255,255,0.35)",
                            textIndent: "0.65em",
                        }}
                    >
                        {BRAND.company.tagline}
                    </div>
                </div>
            )}
        </div>
    );
}
